<template>
  <v-container
    v-if="authenticationMixin.checkedLogin"
    class="content pa-0"
    fluid
  >
    <!-- Hero section -->
    <page-hero
      title="Mijn overzicht"
      intro="Overzicht van wat u reeds behandeld hebt in uw persoonlijke account. Onderdelen die goedgekeurd zijn, kunnen enkel na telefonisch contact mogelijk nog aangepast worden."
    />

    <!-- Content columns -->
    <v-row
      justify="center"
      class="mb-8 pb-8 overzicht-columns"
    >
      <!-- Raadplegen -->
      <v-col
        class="center px-4"
        cols="12"
        md="5"
        lg="4"
      >
        <div class="overzicht-section">
          <div class="overzicht-section-header overzicht-section-header--secondary">
            <v-icon
              color="#424E58"
              size="22"
            >
              mdi-magnify
            </v-icon>
            <h2 class="overzicht-section-title overzicht-section-title--secondary">
              Raadplegen
            </h2>
          </div>
          <div class="overzicht-list">
            <component
              v-for="(item, index) in raadplegenItems"
              :is="item.disabled ? 'div' : 'router-link'"
              class="overzicht-item"
              :class="{ 'overzicht-item--disabled': item.disabled }"
              :key="'r-' + index"
              :to="item.disabled ? undefined : item.route"
            >
              <span class="overzicht-item-title">
                {{ item.title }}
              </span>
              <v-btn
                v-if="!item.disabled"
                color="primary"
                rounded
                size="small"
                variant="outlined"
              >
                Bekijken
              </v-btn>
              <span
                v-else
                class="overzicht-item-hint"
              >
                Binnenkort beschikbaar
              </span>
            </component>
          </div>
        </div>
      </v-col>

      <!-- Samenstellen -->
      <v-col
        class="center px-4"
        cols="12"
        md="7"
        lg="6"
      >
        <div class="overzicht-section">
          <div class="overzicht-section-header overzicht-section-header--gold">
            <v-icon
              color="#907322"
              size="22"
            >
              mdi-clipboard-list-outline
            </v-icon>
            <h2 class="overzicht-section-title overzicht-section-title--gold">
              Samenstellen
            </h2>
          </div>
          <div class="overzicht-list">
            <div
              v-for="(item, index) in samenstellenItems"
              class="overzicht-task"
              :key="'s-' + index"
            >
              <div
                class="overzicht-task-header"
                :class="{ 'overzicht-task-header--expandable': item.expandedContent && item.expandedContent.length > 0 }"
                @click="ToggleExpand(index)"
              >
                <div class="overzicht-task-left">
                  <v-icon
                    v-if="item.expandedContent && item.expandedContent.length > 0"
                    class="overzicht-task-chevron"
                    :class="{ 'overzicht-task-chevron--open': expandedPanels.includes(index) }"
                    color="#907322"
                    size="18"
                  >
                    mdi-chevron-right
                  </v-icon>
                  <span class="overzicht-task-title">
                    {{ item.title }}
                  </span>
                </div>
                <div class="overzicht-task-right">
                  <v-icon
                    :color="item.isLocked ? '#6B8E6F' : '#999'"
                    size="18"
                  >
                    {{ item.isLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                  </v-icon>
                  <span
                    class="overzicht-task-status"
                    :style="{ backgroundColor: item.buttonColor }"
                  >
                    {{ item.buttonText }}
                  </span>
                </div>
              </div>

              <!-- Expanded content -->
              <div
                v-if="expandedPanels.includes(index) && item.expandedContent && item.expandedContent.length > 0"
                class="overzicht-task-content"
              >
                <div
                  v-for="(subItem, subIndex) in item.expandedContent"
                  class="overzicht-sub-item"
                  :key="'sub-' + subIndex"
                >
                  <div class="overzicht-sub-item-header">
                    <span class="overzicht-sub-item-title">
                      {{ subItem.title }}
                    </span>
                    <span
                      v-if="subItem.buttonText"
                      class="overzicht-task-status overzicht-task-status--small"
                      :style="{ backgroundColor: subItem.buttonColor || '#907322' }"
                    >
                      {{ subItem.buttonText }}
                    </span>
                  </div>
                  <div
                    v-if="subItem.details"
                    class="overzicht-sub-item-details"
                  >
                    <div
                      v-for="(detail, dIndex) in subItem.details"
                      class="overzicht-detail"
                      :key="'d-' + dIndex"
                    >
                      <span class="overzicht-detail-label">{{ detail.label }}:</span>
                      <span class="overzicht-detail-value">{{ detail.value }}</span>
                    </div>
                  </div>
                  <div
                    v-if="subItem.image"
                    class="overzicht-sub-item-image"
                  >
                    <v-img
                      :alt="subItem.title"
                      class="rounded"
                      max-width="80"
                      :src="subItem.image"
                    />
                    <span
                      v-if="subItem.price"
                      class="overzicht-detail-value"
                    >
                      {{ subItem.price }}
                    </span>
                  </div>
                  <p
                    v-if="subItem.content"
                    class="overzicht-sub-item-content"
                  >
                    {{ subItem.content }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import authenticationMixin from '@/mixins/authenticationMixin'
import commonMixin from '@/mixins/commonMixin'
import errorMixin from '@/mixins/errorMixin'
import klantService from '@/services/klantService'
import PageHero from '@/components/PageHero.vue'

export default {
  name: 'MijnOverzichtComponent',
  components: {
    PageHero
  },
  mixins: [
    authenticationMixin,
    commonMixin,
    errorMixin
  ],
  data () {
    return {
      dossier: null,
      expandedPanels: [],
      raadplegenItems: [
        {
          title: 'Condoleances',
          route: 'Condoleances',
          disabled: false
        },
        {
          title: 'Aanwezigen koffietafel',
          route: 'Koffietafel',
          disabled: false
        },
        {
          title: 'Video uitvaart',
          route: 'Video',
          disabled: true
        }
      ],
      samenstellenItems: []
    }
  },
  mounted () {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    this.AuthenticationMixin_CheckIfLoggedIn()
    klantService.Init(this)
    this.GetDossier()
  },
  methods: {
    async GetDossier () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetDossier()

      if (resp && (resp.data || resp.data !== '')) {
        this.dossier = resp.data

        if (this.dossier && this.dossier.idDossier) {
          await this.GetOpvolgingTaken(this.dossier.idDossier)
        }
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async GetOpvolgingTaken (idDossier) {
      const resp = await klantService.GetDossierOpvolgingFamilieTaakList(idDossier)

      if (resp && resp.data) {
        this.samenstellenItems = this.MapOpvolgingToSamenstellenItems(resp.data)
      }
    },
    GetButtonColor (item) {
      if (item.uitgevoerd === true || item.datumUitgevoerd !== null) {
        return '#6B8E6F'
      } else if (item.opmerking !== null && item.opmerking !== '') {
        return '#907322'
      } else {
        return '#DEC067'
      }
    },
    GetButtonText (item) {
      if (item.uitgevoerd === true || item.datumUitgevoerd !== null) {
        return 'goedgekeurd'
      } else if (item.opmerking !== null && item.opmerking !== '') {
        return 'nakijken/goedkeuren'
      } else {
        return 'in/aanvullen'
      }
    },
    MapExpandedContent (item) {
      const subData = item.subItems || item.subTaken || item.details

      if (Array.isArray(subData)) {
        return subData.map(subItem => ({
          title: subItem.omschrijving || subItem.naam || subItem.title || '',
          buttonText: this.GetButtonText(subItem),
          buttonColor: this.GetButtonColor(subItem),
          content: subItem.content || subItem.inhoud || '',
          details: subItem.details || null,
          image: subItem.image || subItem.afbeelding || null,
          price: subItem.price || subItem.prijs || null
        }))
      }

      return null
    },
    MapOpvolgingToSamenstellenItems (apiData) {
      if (!Array.isArray(apiData)) {
        return []
      }

      return apiData.map(item => ({
        title: item.opvolgingFamilieTaak?.beschrijving || '',
        isLocked: item.uitgevoerd === true,
        buttonText: this.GetButtonText(item),
        buttonColor: this.GetButtonColor(item),
        expandedContent: this.MapExpandedContent(item)
      }))
    },
    ToggleExpand (index) {
      const item = this.samenstellenItems[index]

      if (!item.expandedContent || item.expandedContent.length === 0) {
        return
      }

      const pos = this.expandedPanels.indexOf(index)

      if (pos === -1) {
        this.expandedPanels.push(index)
      } else {
        this.expandedPanels.splice(pos, 1)
      }
    }
  }
}
</script>

<style scoped>
.overzicht-columns {
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

/* Section header */
.overzicht-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 4px;
  border-bottom: 2px solid;
}

.overzicht-section-header--secondary {
  border-color: #424E58;
}

.overzicht-section-header--gold {
  border-color: #DEC067;
}

.overzicht-section-title {
  font-size: 22px;
  font-weight: 500;
  padding: 0;
  margin: 0;
}

.overzicht-section-title--secondary {
  color: #424E58;
}

.overzicht-section-title--gold {
  color: #907322;
}

/* Raadplegen list */
.overzicht-list {
  display: flex;
  flex-direction: column;
}

.overzicht-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 8px;
  border-bottom: 1px solid #e8e4dc;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease;
}

.overzicht-item:hover {
  background-color: #FBF7EE;
}

.overzicht-item--disabled {
  opacity: 0.5;
  cursor: default;
}

.overzicht-item--disabled:hover {
  background-color: transparent;
}

.overzicht-item-title {
  font-size: 15px;
  font-weight: 500;
  color: #424E58;
}

.overzicht-item-hint {
  font-size: 13px;
  font-style: italic;
  color: #999;
}

/* Samenstellen tasks */
.overzicht-task {
  border-bottom: 1px solid #e8e4dc;
}

.overzicht-task-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 8px;
  gap: 8px;
}

.overzicht-task-header--expandable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.overzicht-task-header--expandable:hover {
  background-color: #FBF7EE;
}

.overzicht-task-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.overzicht-task-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.overzicht-task-chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.overzicht-task-chevron--open {
  transform: rotate(90deg);
}

.overzicht-task-title {
  font-size: 15px;
  font-weight: 500;
  color: #424E58;
}

.overzicht-task-status {
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
}

.overzicht-task-status--small {
  font-size: 11px;
  padding: 2px 8px;
}

/* Expanded content */
.overzicht-task-content {
  padding: 4px 8px 12px 32px;
}

.overzicht-sub-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0ede6;
}

.overzicht-sub-item:last-child {
  border-bottom: none;
}

.overzicht-sub-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.overzicht-sub-item-title {
  font-size: 14px;
  font-weight: 500;
  color: #424E58;
}

.overzicht-sub-item-details {
  margin-top: 4px;
  padding-left: 4px;
}

.overzicht-detail {
  display: flex;
  gap: 6px;
  font-size: 13px;
  margin-bottom: 2px;
}

.overzicht-detail-label {
  font-weight: 500;
  color: #999;
}

.overzicht-detail-value {
  color: #424E58;
}

.overzicht-sub-item-image {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}

.overzicht-sub-item-content {
  font-size: 13px;
  color: #6b6b6b;
  margin: 4px 0 0 0;
  padding: 0;
}
</style>
